// src/services/api.js
// Shared utility functions for making API calls and handling responses
// FIXED: Robust Axios interceptors and correct 401/403 handling

import { config } from '../config'; // Import frontend config
import { getAuthToken, clearAuthData } from '../utils/auth'; // Import auth utils
import axios from 'axios';

const API_BASE_URL = config.apiUrl; 

/**
 * Helper to handle fetch response errors consistently.
 */
export const handleApiError = async (response, resourceName = 'resource') => {
    let errorMsg = `HTTP error ${response.status} fetching ${resourceName}: ${response.statusText}`;
    let errorDetails = null;

    if (response.headers.get("content-type")?.includes("application/json")) {
        try {
            const errData = await response.json();
            errorMsg = errData.message || (errData.errors ? errData.errors.map(e => e.msg).join(', ') : errorMsg);
            errorDetails = errData;
        } catch (parseErr) {
            console.error(`Error parsing JSON error response for ${resourceName}:`, parseErr);
            errorMsg = `Received non-JSON error response (Status: ${response.status} ${response.statusText})`;
        }
    } else {
        try {
            const textBody = await response.text();
            errorMsg = `API Error: ${response.status} ${response.statusText}. Response body: ${textBody.substring(0, 100)}...`;
            errorDetails = textBody;
        } catch (readErr) {
            console.error(`Error reading non-JSON error response for ${resourceName}:`, readErr);
            errorMsg = `API Error: ${response.status} ${response.statusText}. Could not read response body.`;
        }
    }

    // FIX: Only clear auth data on 401 (Unauthorized)
    // 403 means the user is authenticated, but lacks permissions for this specific action.
    if (response.status === 401) {
        console.warn('[API] 401 Unauthorized detected. Clearing auth data.');
        clearAuthData();
    }

    const error = new Error(errorMsg);
    error.status = response.status;
    error.details = errorDetails;
    throw error;
};

/**
 * Generic helper to make authenticated API calls using the Fetch API.
 * Correctly handles file uploads via FormData.
 */
export const authenticatedFetch = async (endpoint, options = {}) => {
    // Robust token retrieval fallback
    let token = getAuthToken();
    if (!token) {
        token = sessionStorage.getItem('token') || localStorage.getItem('token');
    }

    if (!token) {
        const authError = new Error("Authentication token missing."); 
        authError.status = 401; 
        throw authError; 
    }

    const headers = {
        'Authorization': `Bearer ${token}`, 
        ...(options.headers || {}), 
    };

    // If the body is FormData, the browser must set the Content-Type header
    // automatically to include the multipart boundary.
    if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json'; 
    }

    const url = `${API_BASE_URL}${endpoint}`;
    console.log(`[authenticatedFetch] Calling: ${options.method || 'GET'} ${url}`);

    try {
        const response = await fetch(url, { 
            ...options,
            headers: headers, 
            // 🚀 ENTERPRISE FIX: Apply a 5-minute timeout to native fetch requests 
            // to allow heavy AI analytical endpoints to complete without dropping.
            signal: options.signal || AbortSignal.timeout(300000)
        });

        if (!response.ok) {
            await handleApiError(response, endpoint); 
        }
        
        const responseText = await response.text(); 
        
        if (!responseText) {
            return null; 
        }

        try {
            return JSON.parse(responseText); 
        } catch (parseErr) {
            console.error(`Error parsing JSON response body for ${endpoint}:`, parseErr, "Response Text:", responseText); 
            throw new Error(`Invalid JSON response received from ${endpoint}.`); 
        }

    } catch (error) {
        throw error; 
    }
};


// ============================================================================
// AXIOS CONFIGURATION
// ============================================================================

// Create a new Axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  // 🚀 ENTERPRISE FIX: Tell the Axios client to wait up to 5 minutes (300,000ms) 
  // for the backend to stream large AI reports.
  timeout: 300000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request Interceptor: Automatically add the auth token
api.interceptors.request.use(
  (config) => {
    let token = null;
    
    // Try utility first, fallback to direct storage access
    try {
        token = getAuthToken();
    } catch (e) {
        console.warn('[Axios Interceptor] getAuthToken util failed', e);
    }
    
    if (!token) {
        token = sessionStorage.getItem('token') || localStorage.getItem('token');
    }

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    } else {
      console.warn(`[Axios Interceptor] Warning: No token found for request to ${config.url}`);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor: Handle global errors (401/403)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
        if (error.response.status === 401) {
            console.warn('[Axios Interceptor] 401 Unauthorized. Clearing auth data.');
            clearAuthData();
            // Optional: Force reload to login screen
            // window.location.href = '/auth/sign-in';
        } else if (error.response.status === 403) {
            console.warn(`[Axios Interceptor] 403 Forbidden on ${error.config.url}. User lacks permissions.`);
        }
    }
    return Promise.reject(error);
  }
);

export default api;